import React, { useState } from 'react';
import type { DiagnosisResult } from '@/utils/diagnosisEngine';

interface LeadUnlockSectionProps {
  result: DiagnosisResult;
  onReset: () => void;
}

export default function LeadUnlockSection({ result, onReset }: LeadUnlockSectionProps) {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [agree, setAgree] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Format phone number
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, '').slice(0, 11);
    if (raw.length <= 3) {
      setPhone(raw);
    } else if (raw.length <= 7) {
      setPhone(`${raw.slice(0, 3)}-${raw.slice(3)}`);
    } else {
      setPhone(`${raw.slice(0, 3)}-${raw.slice(3, 7)}-${raw.slice(7)}`);
    }
  };

  const handleUnlockSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setSubmitError('성함을 입력해 주세요.');
      return;
    }
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    if (cleanPhone.length < 10) {
      setSubmitError('올바른 휴대폰 번호를 입력해 주세요.');
      return;
    }
    if (!agree) {
      setSubmitError('개인정보 수집 및 심층 분석 리포트 수신에 동의해 주세요.');
      return;
    }

    setSubmitError('');
    setSubmitting(true);

    // 옵션 B 핵심: 브라우저 화면에서 즉시 전체 심층 분석 리포트 언락!
    setIsUnlocked(true);

    // 비동기로 백엔드에 리드 전송 (실패해도 화면 언락 유지)
    try {
      const payload = {
        name,
        phone,
        trackId: result.trackId,
        trackName: result.trackName,
        overallScore: result.overallScore,
        percentile: result.percentile,
        timestamp: new Date().toISOString(),
        source: '자가진단_옵션B_심층언락',
      };
      await fetch('/api/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {/* ignore network error */});
    } catch {
      // ignore
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* 🔒 LOCKED STATE (옵션 B 리드 수집 폼) */}
      {!isUnlocked ? (
        <div className="relative overflow-hidden rounded-2xl border-2 border-brand-200 dark:border-brand-800/80 bg-gradient-to-b from-white via-indigo-50/30 to-purple-50/20 dark:from-gray-900 dark:via-gray-900/90 dark:to-indigo-950/30 p-6 md:p-8 shadow-md">
          {/* Header */}
          <div className="text-center max-w-xl mx-auto mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-100 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300 mb-3 shadow-sm">
              <i className="ri-lock-2-fill text-2xl"></i>
            </div>
            <h4 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
              🔒 30만 원장 실측 전체 심층 분석 리포트 언락
            </h4>
            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed break-keep">
              이름과 연락처를 입력하시면 <strong className="text-brand-700 dark:text-brand-400">원장 매칭 채용 기업 7개사, 경력별 급여 관측치, 3단계 맞춤 로드맵</strong>이 브라우저 화면에서 즉시 열립니다.
            </p>
          </div>

          {/* Included Unlocked Value Items */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mx-auto mb-7">
            <div className="flex items-center gap-2.5 p-3 rounded-xl bg-white/90 dark:bg-gray-800/80 border border-gray-200/80 dark:border-gray-700/80 text-xs">
              <i className="ri-building-4-line text-indigo-600 dark:text-indigo-400 text-base"></i>
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                실제 채용 중인 7개 기업 리스트
              </span>
            </div>
            <div className="flex items-center gap-2.5 p-3 rounded-xl bg-white/90 dark:bg-gray-800/80 border border-gray-200/80 dark:border-gray-700/80 text-xs">
              <i className="ri-funds-line text-indigo-600 dark:text-indigo-400 text-base"></i>
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                국민연금 사업장 실측 연봉 밴드
              </span>
            </div>
            <div className="flex items-center gap-2.5 p-3 rounded-xl bg-white/90 dark:bg-gray-800/80 border border-gray-200/80 dark:border-gray-700/80 text-xs">
              <i className="ri-medal-line text-indigo-600 dark:text-indigo-400 text-base"></i>
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                필수 취득 권장 자격증 4종
              </span>
            </div>
            <div className="flex items-center gap-2.5 p-3 rounded-xl bg-white/90 dark:bg-gray-800/80 border border-gray-200/80 dark:border-gray-700/80 text-xs">
              <i className="ri-route-line text-indigo-600 dark:text-indigo-400 text-base"></i>
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                3단계 압축 캡스톤 커리큘럼
              </span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleUnlockSubmit} className="max-w-md mx-auto space-y-4">
            {submitError && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-xs text-red-700 dark:text-red-300 font-medium">
                {submitError}
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                성함 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="홍길동"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                연락처 (휴대폰 번호) <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                placeholder="010-0000-0000"
                value={phone}
                onChange={handlePhoneChange}
                maxLength={13}
                className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>

            <div className="flex items-start gap-2 pt-1">
              <input
                type="checkbox"
                id="agree_opt_b"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded text-brand-600 focus:ring-brand-500 cursor-pointer"
              />
              <label htmlFor="agree_opt_b" className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer leading-tight">
                [필수] 개인정보 수집 및 심층 분석 리포트 열람 목적 안내에 동의합니다. (상담 외 무단 스팸 발송 없음)
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 px-6 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-brand-600 via-indigo-600 to-purple-600 hover:opacity-95 shadow-md shadow-brand-500/20 active:scale-[0.99] transition-all cursor-pointer flex items-center justify-center gap-2"
            >
              <i className="ri-flashlight-fill text-yellow-300 text-base"></i>
              즉시 전체 심층 분석 리포트 언락하기
            </button>
          </form>
        </div>
      ) : (
        /* 🔓 UNLOCKED STATE (심층 분석 전체 리포트) */
        <div className="space-y-6 animate-fadeIn">
          {/* Unlocked Banner */}
          <div className="rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-700 text-white p-5 md:p-6 shadow-md flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                <i className="ri-lock-unlock-line text-2xl text-yellow-300"></i>
              </div>
              <div>
                <span className="text-xs font-bold text-emerald-200 uppercase tracking-wider block">
                  심층 리포트 활성화 완료
                </span>
                <h4 className="text-lg md:text-xl font-extrabold">
                  {name} 님의 {result.trackName} 맞춤형 심층 분석 보고서
                </h4>
              </div>
            </div>
            <span className="text-xs px-3 py-1 rounded-full bg-white/20 text-white font-medium">
              30만 원장 실시간 매칭
            </span>
          </div>

          {/* Section 1: Top Hiring Companies & Positions */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5 md:p-7 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <i className="ri-building-line text-brand-600 text-xl"></i>
                <h5 className="font-bold text-base md:text-lg text-gray-900 dark:text-gray-100">
                  원장 DB 매칭 채용 기업 & 주요 채용처
                </h5>
              </div>
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                채용공고 {result.stats.observedJobs.toLocaleString()}건 매칭
              </span>
            </div>

            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
              최근 30만 건 원장 데이터(채용공고 4,012건 및 국민연금 2,611개사) 중 {result.trackName} 직무 인력을 활발히 충원하고 있는 대표 테크 기업입니다.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {result.topHiringCompanies.map((comp) => (
                <div
                  key={comp}
                  className="p-3.5 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/50 hover:border-brand-400 transition-colors flex items-center gap-2.5"
                >
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-950/70 text-indigo-700 dark:text-indigo-300 flex items-center justify-center font-bold text-xs shrink-0">
                    {comp.slice(0, 1)}
                  </div>
                  <span className="font-bold text-sm text-gray-800 dark:text-gray-200 truncate">
                    {comp}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Section 2: Real Observed Salary Bands & Pension Data */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5 md:p-7 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <i className="ri-money-dollar-box-line text-brand-600 text-xl"></i>
                <h5 className="font-bold text-base md:text-lg text-gray-900 dark:text-gray-100">
                  국민연금 사업장 실측 연봉 관측 밴드
                </h5>
              </div>
              <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                평균 월 고지액 기준 실측
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mb-4">
              <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-800/40">
                <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                  신입 / 입문자 초임 밴드
                </span>
                <span className="text-lg md:text-xl font-extrabold text-gray-900 dark:text-gray-100">
                  {result.stats.salaryBand.entry}
                </span>
                <span className="text-[11px] text-gray-400 block mt-1">상위 20% 초임 구간</span>
              </div>
              <div className="p-4 rounded-xl border border-indigo-200 dark:border-indigo-900/60 bg-indigo-50/50 dark:bg-indigo-950/30">
                <span className="text-xs text-indigo-600 dark:text-indigo-400 block mb-1 font-semibold">
                  3년 차 주니어 이직 밴드
                </span>
                <span className="text-lg md:text-xl font-extrabold text-indigo-700 dark:text-indigo-300">
                  {result.stats.salaryBand.junior_3yr}
                </span>
                <span className="text-[11px] text-indigo-500/80 dark:text-indigo-400/80 block mt-1">
                  연봉 리프트 +30% 구간
                </span>
              </div>
              <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-800/40">
                <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                  5년 차 이상 시니어 밴드
                </span>
                <span className="text-lg md:text-xl font-extrabold text-gray-900 dark:text-gray-100">
                  {result.stats.salaryBand.senior_5yr}
                </span>
                <span className="text-[11px] text-gray-400 block mt-1">테크 리드 및 코어 설계자</span>
              </div>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              * 본 수치는 단순 설문 예측이 아닌 국민연금공단 사업장 가입자 현황 및 고지금액원 기반 실측 통계입니다.
            </p>
          </div>

          {/* Section 3: Recommended Certifications & 3-Step Roadmap */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Certs */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5 md:p-6 shadow-sm flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-award-line text-brand-600 text-xl"></i>
                  <h5 className="font-bold text-base text-gray-900 dark:text-gray-100">
                    서류 가산점 우대 자격증
                  </h5>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
                  원장 3.5만 건 훈련 이력 기준, 서류 통과율을 최대 2.6배 높이는 핵심 자격증입니다.
                </p>
                <div className="flex flex-wrap gap-2">
                  {result.recommendedCerts.map((cert) => (
                    <span
                      key={cert}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700"
                    >
                      {cert}
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 text-[11px] text-gray-400">
                훈련 과정 내 자격증 응시료 지원 혜택 포함
              </div>
            </div>

            {/* Roadmap */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5 md:p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-compass-3-line text-brand-600 text-xl"></i>
                <h5 className="font-bold text-base text-gray-900 dark:text-gray-100">
                  3단계 맞춤형 캡스톤 로드맵
                </h5>
              </div>
              <div className="space-y-2.5">
                {result.curriculumRoadmap.map((step, idx) => (
                  <div
                    key={idx}
                    className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-800 dark:text-gray-200 leading-snug"
                  >
                    {step}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Section 4: Detailed Analysis Narrative */}
          <div className="bg-indigo-50/70 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/60 rounded-2xl p-5 md:p-7 shadow-sm">
            <h5 className="font-bold text-base md:text-lg text-indigo-950 dark:text-indigo-200 mb-2 flex items-center gap-2">
              <i className="ri-lightbulb-flash-line text-indigo-600 dark:text-indigo-400"></i>
              다봄 룰 엔진 심층 진단 총평 & 액션 플랜
            </h5>
            <div className="space-y-3 text-xs md:text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              <p>
                <strong>채용 동향 인사이트:</strong> {result.detailAnalysis.hiringInsight}
              </p>
              <p>
                <strong>처우 및 급여 분석:</strong> {result.detailAnalysis.salaryInsight}
              </p>
              <p className="font-semibold text-brand-700 dark:text-brand-300">
                💡 추천 실행 액션: {result.detailAnalysis.actionItem}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
            <button
              type="button"
              onClick={() => window.print()}
              className="w-full sm:w-auto flex-1 py-3 px-4 rounded-xl text-sm font-bold border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer flex items-center justify-center gap-2"
            >
              <i className="ri-printer-line"></i>
              리포트 인쇄 / PDF 저장
            </button>
            <button
              type="button"
              onClick={onReset}
              className="w-full sm:w-auto py-3 px-5 rounded-xl text-sm font-bold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors cursor-pointer"
            >
              다시 진단하기
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
